import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Landmark, PiggyBank } from 'lucide-react';
import { Heading } from '../../../layout';
import ScheduleCalendar from '../../../reusable/ScheduleCalendar';
import { useUIColors } from '../../../../hooks/useTheme';
import financeInvestorsAPI from '../../../../services/financeInvestorsAPI';
import financePublicFundsAPI from '../../../../services/financePublicFundsAPI';
import FinancingTrackPanel from './FinancingTrackPanel';

const NAV_ITEMS = [
  {
    key: 'investors',
    label: 'Inversionistas',
    icon: PiggyBank,
    entityLabel: 'Inversionista',
    entitiesLabel: 'Inversionistas',
    api: financeInvestorsAPI,
  },
  {
    key: 'public_funds',
    label: 'Fondos públicos',
    icon: Landmark,
    entityLabel: 'Fondo público',
    entitiesLabel: 'Fondos públicos',
    api: financePublicFundsAPI,
  },
];

const toCalendarEvents = (rows, trackKey, trackLabel, trackColor) =>
  (Array.isArray(rows) ? rows : [])
    .map((act) => {
      const date = act.follow_up_reminder_date || act.next_step_date || act.activity_date || '';
      if (!date) return null;
      const due = String(act.due_state || '').toLowerCase();
      let color = trackColor;
      if (due === 'vencida') color = '#dc2626';
      else if (due === 'completa') color = '#16a34a';
      else if (due === 'por vencer') color = '#f59e0b';
      return {
        date: String(date).slice(0, 10),
        title: act.action || 'Actividad',
        subtitle: `${trackLabel}: ${act.parent_name || act.contact_name || 'Sin nombre'}`,
        status: act.due_state || act.status,
        color,
        raw: { ...act, trackKey },
      };
    })
    .filter(Boolean);

const FinanceFinancingProspectingWorkspace = ({ isDarkMode = false }) => {
  const ui = useUIColors();
  const [section, setSection] = useState('investors');
  const [investorTracking, setInvestorTracking] = useState([]);
  const [fundTracking, setFundTracking] = useState([]);
  const [openEntityRequest, setOpenEntityRequest] = useState(null);
  const active = NAV_ITEMS.find((item) => item.key === section) || NAV_ITEMS[0];

  const refreshCalendar = useCallback(async () => {
    try {
      const [inv, funds] = await Promise.all([
        financeInvestorsAPI.getTracking(),
        financePublicFundsAPI.getTracking(),
      ]);
      setInvestorTracking(Array.isArray(inv) ? inv : []);
      setFundTracking(Array.isArray(funds) ? funds : []);
    } catch {
      /* panel surfaces local errors */
    }
  }, []);

  useEffect(() => {
    refreshCalendar();
  }, [refreshCalendar]);

  const calendarEvents = useMemo(
    () => [
      ...toCalendarEvents(investorTracking, 'investors', 'Inversionista', '#2563eb'),
      ...toCalendarEvents(fundTracking, 'public_funds', 'Fondo', '#7c3aed'),
    ],
    [investorTracking, fundTracking]
  );

  return (
    <div>
      <div style={{ marginBottom: 16 }}>
        <Heading level={2} isDarkMode={isDarkMode} style={{ marginBottom: 6 }}>
          Financiamiento
        </Heading>
        <p style={{ margin: 0, maxWidth: 720, fontSize: 13, color: ui.muted, lineHeight: 1.45 }}>
          Prospección de inversionistas y fondos públicos, con datos separados del área comercial.
        </p>
      </div>

      <div style={{ marginBottom: 16 }}>
        <ScheduleCalendar
          events={calendarEvents}
          isDarkMode={isDarkMode}
          title="Calendario de financiamiento"
          allowedViews={['week', 'biweek', 'month']}
          defaultView="biweek"
          emptyLabel="Sin actividades de inversionistas ni fondos en este período"
          onEventClick={(ev) => {
            const trackKey = ev?.raw?.trackKey;
            const parentId = Number(ev?.raw?.parent_id || 0);
            if (!trackKey || !parentId) return;
            setSection(trackKey);
            setOpenEntityRequest({ id: parentId, token: Date.now() });
          }}
        />
      </div>

      <div
        style={{
          display: 'flex',
          gap: 8,
          marginBottom: 16,
          flexWrap: 'wrap',
          padding: 8,
          borderRadius: 12,
          border: `1px solid ${ui.border}`,
          background: ui.panel || ui.page,
        }}
      >
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          const isActive = section === item.key;
          return (
            <button
              key={item.key}
              type="button"
              onClick={() => setSection(item.key)}
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                gap: 8,
                height: 40,
                padding: '0 14px',
                borderRadius: 8,
                border: `1px solid ${isActive ? '#16a34a' : ui.border}`,
                background: isActive
                  ? isDarkMode
                    ? 'rgba(22, 163, 74, 0.18)'
                    : '#f0fdf4'
                  : 'transparent',
                color: ui.text,
                fontWeight: 600,
                fontSize: 13,
                cursor: 'pointer',
              }}
            >
              <Icon size={16} />
              {item.label}
            </button>
          );
        })}
      </div>

      <FinancingTrackPanel
        key={active.key}
        trackKey={active.key}
        api={active.api}
        isDarkMode={isDarkMode}
        entityLabel={active.entityLabel}
        entitiesLabel={active.entitiesLabel}
        openEntityRequest={section === active.key ? openEntityRequest : null}
        onDataMutated={refreshCalendar}
      />
    </div>
  );
};

export default FinanceFinancingProspectingWorkspace;
